import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuditPlan, useAuditPlans, useAuditors, usePlanMutations, type AuditPlan, type AuditPlanInput, type AuditPlanStatus, type RecurrenceFrequency } from '../api/admin'
import { fillPath, useCompanies, useTemplates } from '../api/hooks'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Modal, StatusBadge, fmtDate, fmtDateTime } from '../components/ui'
import DateInput from '../components/DateInput'
import { errMsg } from './TemplatesPage'

const statusColor: Record<AuditPlanStatus, string> = { Planned: '#2a78d6', InProgress: '#eda100', Completed: '#008300', Cancelled: '#9aa3ad' }
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default function PlanningPage() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n) }
  const month = params.get('month') ?? iso(new Date()).slice(0, 7)
  const [year, mon] = month.split('-').map(Number)
  const first = new Date(year, mon - 1, 1)
  const gridStartMs = new Date(year, mon - 1, 1 - ((first.getDay() + 6) % 7)).getTime()
  const gridStart = new Date(gridStartMs)
  const days = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStartMs); d.setDate(d.getDate() + i); return d })
  const filters = { from: iso(gridStart), to: iso(days[41]), companyId: params.get('companyId') ?? '', mineOnly: params.get('mine') === '1', includeCancelled: params.get('cancelled') === '1' }
  const plans = useAuditPlans(filters)
  const companies = useCompanies()
  const canEdit = user?.roles.some((r) => ['TenantAdmin', 'SuperAdmin', 'Auditor'].includes(r.role)) ?? false
  const selectedId = params.get('plan') ?? undefined
  const [editing, setEditing] = useState<{ id?: string; input: AuditPlanInput } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const m = usePlanMutations()

  const shift = (delta: number) => { const d = new Date(year, mon - 1 + delta, 1); set('month', iso(d).slice(0, 7)) }
  const byDay = useMemo(() => {
    const map = new Map<string, AuditPlan[]>()
    for (const p of plans.data ?? []) {
      const start = new Date(p.plannedStartDate)
      const end = p.plannedEndDate ? new Date(p.plannedEndDate) : start
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const k = iso(d)
        map.set(k, [...(map.get(k) ?? []), p])
      }
    }
    return map
  }, [plans.data])
  const today = iso(new Date())
  const monthName = first.toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-GB', { month: 'long', year: 'numeric' })
  const weekdays = Array.from({ length: 7 }, (_, i) => { const d = new Date(2024, 0, 1 + i); return d.toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-GB', { weekday: 'short' }) })
  const listInMonth = (plans.data ?? []).filter((p) => p.plannedStartDate.slice(0, 7) === month).sort((a, b) => a.plannedStartDate.localeCompare(b.plannedStartDate))

  const newPlan = (date?: string) => {
    setError(null)
    setEditing({ input: { companyId: companies.data?.[0]?.id ?? '', siteId: null, formTemplateId: '', title: '', plannedStartDate: date ?? today, plannedEndDate: null, notes: '', auditors: user ? [{ userId: user.id, isLead: true }] : [], recurrence: null } })
  }

  return (
    <div className="stack">
      <div className="row between wrap">
        <h2>{t('planning.title')}</h2>
        {canEdit ? <button onClick={() => newPlan()}>{t('planning.new')}</button> : null}
      </div>
      <div className="card filters">
        <div className="row">
          <button className="secondary small-btn" onClick={() => shift(-1)}>‹</button>
          <b style={{ minWidth: 150, textAlign: 'center' }}>{monthName}</b>
          <button className="secondary small-btn" onClick={() => shift(1)}>›</button>
          <button className="ghost small-btn" onClick={() => set('month', '')}>{t('planning.today')}</button>
        </div>
        <select value={filters.companyId} onChange={(e) => set('companyId', e.target.value)}>
          <option value="">{t('common.allCompanies')}</option>
          {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label className="row"><input type="checkbox" checked={filters.mineOnly} onChange={(e) => set('mine', e.target.checked ? '1' : '')} /> {t('planning.mine')}</label>
        <label className="row"><input type="checkbox" checked={filters.includeCancelled} onChange={(e) => set('cancelled', e.target.checked ? '1' : '')} /> {t('planning.showCancelled')}</label>
      </div>
      {error ? <p className="error">{error}</p> : null}

      <div className="grid detail">
        <div className="card">
          <div className="calendar">
            {weekdays.map((w) => <div key={w} className="cal-head">{w}</div>)}
            {days.map((d) => {
              const k = iso(d)
              const inMonth = d.getMonth() === mon - 1
              const items = byDay.get(k) ?? []
              return (
                <div key={k} className={`cal-day ${inMonth ? '' : 'other'} ${k === today ? 'today' : ''}`} onDoubleClick={() => canEdit && newPlan(k)}>
                  <div className="cal-date">{d.getDate()}</div>
                  {items.slice(0, 3).map((p) => (
                    <button key={p.id} className="cal-chip" style={{ borderLeftColor: statusColor[p.status], opacity: p.status === 'Cancelled' ? 0.5 : 1 }} onClick={() => set('plan', p.id)} title={`${p.companyName} · ${p.templateCode}`}>
                      {p.companyName}
                    </button>
                  ))}
                  {items.length > 3 ? <div className="muted small">+{items.length - 3}</div> : null}
                </div>
              )
            })}
          </div>
          <p className="muted small" style={{ marginTop: 8 }}>{t('planning.calendarHint')}</p>
        </div>

        <div className="card stack">
          <h3>{t('planning.listTitle', { month: monthName })}</h3>
          {listInMonth.length === 0 ? <p className="muted small">{t('planning.empty')}</p> : listInMonth.map((p) => (
            <button key={p.id} className="nav-item" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }} onClick={() => set('plan', p.id)}>
              <span><b>{fmtDate(p.plannedStartDate)}</b>{p.plannedEndDate && p.plannedEndDate !== p.plannedStartDate ? ` – ${fmtDate(p.plannedEndDate)}` : ''} <Badge color={statusColor[p.status]}>{t(`planStatus.${p.status}`)}</Badge>{p.isOverdue ? <Badge color="#e34948">{t('planning.overdue')}</Badge> : null}</span>
              <span className="small">{p.companyName}{p.siteName ? ` · ${p.siteName}` : ''} · {p.templateCode}</span>
              <span className="muted small">{p.auditors.map((a) => a.fullName).join(', ') || t('planning.noAuditor')}</span>
            </button>
          ))}
        </div>
      </div>

      {selectedId ? <PlanDetail id={selectedId} canEdit={canEdit} onClose={() => set('plan', '')} onEdit={(p) => { setError(null); setEditing({ id: p.id, input: { companyId: p.companyId, siteId: p.siteId ?? null, formTemplateId: p.formTemplateId, title: p.title ?? '', plannedStartDate: p.plannedStartDate, plannedEndDate: p.plannedEndDate ?? null, notes: p.notes ?? '', auditors: p.auditors.map((a) => ({ userId: a.userId, isLead: a.isLead })), recurrence: p.recurrence ?? null } }) }} onStart={(p) => { setError(null); m.start.mutateAsync(p.id).then((inst) => navigate(fillPath(inst))).catch((e: unknown) => setError(errMsg(e))) }} onError={setError} /> : null}

      {editing ? (
        <PlanEditor draft={editing} onClose={() => setEditing(null)} onSave={async (input, applyToSeries) => {
          setError(null)
          try {
            if (editing.id) await m.update.mutateAsync({ id: editing.id, input, applyToSeries })
            else await m.create.mutateAsync(input)
            setEditing(null)
          } catch (e) {
            setError(errMsg(e))
          }
        }} />
      ) : null}
    </div>
  )
}

function PlanDetail({ id, canEdit, onClose, onEdit, onStart, onError }: { id: string; canEdit: boolean; onClose: () => void; onEdit: (p: AuditPlan) => void; onStart: (p: AuditPlan) => void; onError: (s: string | null) => void }) {
  const { t } = useTranslation()
  const plan = useAuditPlan(id)
  const m = usePlanMutations()
  const [series, setSeries] = useState(false)
  const p = plan.data
  if (!p) return <Modal title={t('common.loading')} onClose={onClose}><p>{t('common.loading')}</p></Modal>
  const inSeries = !!(p.parentPlanId || p.recurrence)
  const run = (fn: () => Promise<unknown>) => { onError(null); fn().then(onClose).catch((e: unknown) => onError(errMsg(e))) }
  return (
    <Modal title={`${p.companyName} · ${fmtDate(p.plannedStartDate)}`} onClose={onClose} width={620}>
      <div className="row wrap"><Badge color={statusColor[p.status]}>{t(`planStatus.${p.status}`)}</Badge>{p.isOverdue ? <Badge color="#e34948">{t('planning.overdue')}</Badge> : null}{inSeries ? <Badge>{t('planning.series')}</Badge> : null}</div>
      <dl className="dl">
        <div><dt>{t('reports.template')}</dt><dd>{p.templateCode} — {p.templateName}</dd></div>
        {p.siteName ? <div><dt>{t('companies.site')}</dt><dd>{p.siteName}</dd></div> : null}
        {p.title ? <div><dt>{t('reports.title')}</dt><dd>{p.title}</dd></div> : null}
        <div><dt>{t('planning.dates')}</dt><dd>{fmtDate(p.plannedStartDate)}{p.plannedEndDate ? ` – ${fmtDate(p.plannedEndDate)}` : ''}</dd></div>
        <div><dt>{t('planning.auditors')}</dt><dd>{p.auditors.map((a) => `${a.fullName}${a.isLead ? ` (${t('planning.lead')})` : ''}`).join(', ') || '–'}</dd></div>
        {p.recurrence ? <div><dt>{t('planning.recurrence')}</dt><dd>{t(`recurrence.${p.recurrence.frequency}`)} · {t('planning.every', { n: p.recurrence.interval })}{p.recurrence.count ? ` · ${p.recurrence.count}×` : ''}</dd></div> : null}
        {p.notes ? <div><dt>{t('reports.comment')}</dt><dd>{p.notes}</dd></div> : null}
        {p.formInstanceId ? <div><dt>{t('planning.report')}</dt><dd><Link to={`/reports/${p.formInstanceId}`}>{p.reportCode ?? t('reports.open')}</Link> {p.reportStatus ? <StatusBadge status={p.reportStatus} /> : null}</dd></div> : null}
        {p.reminderSentAt ? <div><dt>{t('planning.reminder')}</dt><dd>{fmtDateTime(p.reminderSentAt)}</dd></div> : null}
      </dl>
      {canEdit ? (
        <>
          {inSeries && p.status === 'Planned' ? <label className="row"><input type="checkbox" checked={series} onChange={(e) => setSeries(e.target.checked)} /> {t('planning.applyToSeries')}</label> : null}
          <div className="row wrap" style={{ justifyContent: 'flex-end' }}>
            {p.status !== 'Cancelled' && p.status !== 'Completed' ? <button onClick={() => onStart(p)} disabled={m.start.isPending}>{p.formInstanceId ? t('planning.openReport') : t('planning.startAudit')}</button> : null}
            {p.status === 'Planned' ? <button className="secondary" onClick={() => onEdit(p)}>{t('planning.edit')}</button> : null}
            {p.status === 'Planned' ? <button className="secondary" onClick={() => run(() => m.setStatus.mutateAsync({ id: p.id, status: 'Cancelled', applyToSeries: series }))}>{t('planning.cancel')}</button> : null}
            {p.status === 'Cancelled' ? <button className="secondary" onClick={() => run(() => m.setStatus.mutateAsync({ id: p.id, status: 'Planned', applyToSeries: false }))}>{t('planning.reactivate')}</button> : null}
            {!p.formInstanceId ? <button className="danger" onClick={() => { if (confirm(t('common.deleteConfirm'))) run(() => m.remove.mutateAsync({ id: p.id, applyToSeries: series })) }}>{t('common.delete')}</button> : null}
          </div>
        </>
      ) : null}
    </Modal>
  )
}

function PlanEditor({ draft, onClose, onSave }: { draft: { id?: string; input: AuditPlanInput }; onClose: () => void; onSave: (input: AuditPlanInput, applyToSeries: boolean) => Promise<void> }) {
  const { t } = useTranslation()
  const [d, setD] = useState<AuditPlanInput>(draft.input)
  const [series, setSeries] = useState(false)
  const [recurring, setRecurring] = useState(!!draft.input.recurrence)
  const companies = useCompanies()
  const templates = useTemplates()
  const auditors = useAuditors()
  const company = companies.data?.find((c) => c.id === d.companyId)
  const toggleAuditor = (userId: string) => setD({ ...d, auditors: d.auditors.some((a) => a.userId === userId) ? d.auditors.filter((a) => a.userId !== userId) : [...d.auditors, { userId, isLead: d.auditors.length === 0 }] })
  const setLead = (userId: string) => setD({ ...d, auditors: d.auditors.map((a) => ({ ...a, isLead: a.userId === userId })) })
  const valid = d.companyId && d.formTemplateId && d.plannedStartDate
  return (
    <Modal title={draft.id ? t('planning.edit') : t('planning.new')} onClose={onClose} width={720}>
      <div className="grid two">
        <label>{t('companies.name')}
          <select value={d.companyId} onChange={(e) => setD({ ...d, companyId: e.target.value, siteId: null })}>
            <option value="">{t('audits.chooseCompany')}</option>
            {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label>{t('companies.site')}
          <select value={d.siteId ?? ''} disabled={!company?.sites.length} onChange={(e) => setD({ ...d, siteId: e.target.value || null })}>
            <option value="">{t('common.none')}</option>
            {company?.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label>{t('reports.template')}
          <select value={d.formTemplateId} onChange={(e) => setD({ ...d, formTemplateId: e.target.value })}>
            <option value="">{t('audits.chooseTemplate')}</option>
            {templates.data?.filter((x) => x.currentVersionId).map((x) => <option key={x.id} value={x.id}>{x.code} — {x.name}</option>)}
          </select>
        </label>
        <label>{t('reports.title')}<input value={d.title ?? ''} onChange={(e) => setD({ ...d, title: e.target.value })} /></label>
        <label>{t('reports.from')}<DateInput value={d.plannedStartDate} onChange={(v) => setD({ ...d, plannedStartDate: v })} /></label>
        <label>{t('reports.to')}<DateInput value={d.plannedEndDate ?? ''} min={d.plannedStartDate} onChange={(v) => setD({ ...d, plannedEndDate: v || null })} /></label>
      </div>
      <div>
        <div className="small" style={{ marginBottom: 4 }}>{t('planning.auditors')}</div>
        <div className="row wrap">
          {auditors.data?.map((a) => {
            const on = d.auditors.find((x) => x.userId === a.id)
            return (
              <span key={a.id} className={`chip ${on ? 'on' : ''}`}>
                <label className="row" style={{ gap: 4 }}><input type="checkbox" checked={!!on} onChange={() => toggleAuditor(a.id)} /> {a.fullName}</label>
                {on ? <label className="row" title={t('planning.lead')} style={{ gap: 2 }}><input type="radio" name="lead" checked={on.isLead} onChange={() => setLead(a.id)} /> <span className="muted small">{t('planning.lead')}</span></label> : null}
              </span>
            )
          })}
        </div>
      </div>
      <label>{t('reports.comment')}<textarea rows={2} value={d.notes ?? ''} onChange={(e) => setD({ ...d, notes: e.target.value })} /></label>
      {!draft.id ? (
        <>
          <label className="row"><input type="checkbox" checked={recurring} onChange={(e) => { setRecurring(e.target.checked); setD({ ...d, recurrence: e.target.checked ? { frequency: 'Monthly', interval: 1, count: 12 } : null }) }} /> {t('planning.recurring')}</label>
          {recurring && d.recurrence ? (
            <div className="grid two">
              <label>{t('planning.frequency')}
                <select value={d.recurrence.frequency} onChange={(e) => setD({ ...d, recurrence: { ...d.recurrence!, frequency: e.target.value as RecurrenceFrequency } })}>
                  {(['Weekly', 'Monthly', 'Quarterly', 'Yearly'] as RecurrenceFrequency[]).map((f) => <option key={f} value={f}>{t(`recurrence.${f}`)}</option>)}
                </select>
              </label>
              <label>{t('planning.interval')}<input type="number" min={1} max={12} value={d.recurrence.interval} onChange={(e) => setD({ ...d, recurrence: { ...d.recurrence!, interval: Number(e.target.value) } })} /></label>
              <label>{t('planning.count')}<input type="number" min={1} max={60} value={d.recurrence.count ?? ''} onChange={(e) => setD({ ...d, recurrence: { ...d.recurrence!, count: e.target.value ? Number(e.target.value) : null } })} /></label>
              <label>{t('planning.until')}<DateInput value={d.recurrence.until ?? ''} onChange={(v) => setD({ ...d, recurrence: { ...d.recurrence!, until: v || null } })} /></label>
            </div>
          ) : null}
        </>
      ) : (draft.input.recurrence ? <label className="row"><input type="checkbox" checked={series} onChange={(e) => setSeries(e.target.checked)} /> {t('planning.applyToSeries')}</label> : null)}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="secondary" onClick={onClose}>{t('common.cancel')}</button>
        <button disabled={!valid} onClick={() => void onSave({ ...d, title: d.title || undefined, notes: d.notes || undefined }, series)}>{t('common.save')}</button>
      </div>
    </Modal>
  )
}
